import type { GetStaticPaths, GetStaticProps } from "next";
import { ArticleWrapper } from "@orlandohealth/build-kit";
import { DynamicLink, SectionPage } from "@orlandohealth/build-kit";
import { BlogStickyShare } from "@orlandohealth/build-kit";
import { RelatedArticles } from "@orlandohealth/build-kit";
import { getHubTaxonomyTags, articleBelongsToTaxonomyTree } from "@orlandohealth/build-kit";

import { BuilderContent, BuilderComponent, builder } from "@builder.io/react";
import { BlogAuthorByNpi, findProviderByNpi } from "@orlandohealth/build-kit";
import { BlogCTA } from "@orlandohealth/build-kit";
import { BlogNavigation } from "@orlandohealth/build-kit";
import { TaxonomyTags } from "@orlandohealth/build-kit";
import { Icon } from "@orlandohealth/build-kit";
import React from "react";
import {
  ArticleHeroMedia,
  getArticleHeroImageUrl,
  resolveArticleHeroMedia,
} from "@orlandohealth/build-kit";
import { EventMicrodata } from "@orlandohealth/build-kit";
import {
  getAllBuilderContent,
  buildBreadcrumbsFromPage,
  getServiceContentHubSlugs,
  normalizeSiteId,
  getAllContentHubArticlesCached,
  getContentHubArticleFetchOptions,
  getContentHubArticleSingleFetchOptions,
  prepareContentHubArticles,
  resolveContentHubArticleForLocale,
  mergeTaxonomyTree,
  fetchContentHubArticleBySlug,
  contentHubSlugsEqual,
  ensureCurrentArticleInList,
  getTaxonomyForBuild,
} from "@orlandohealth/build-kit";

// Create a ArticlePage class that extends SectionPage
class ArticlePage extends SectionPage {
  static async getStaticProps(
    urlPath: string,
    slug: string,
    locale?: string,
    defaultLocale?: string,
    model: string = "article"
  ) {
    return super.getStaticProps(urlPath, slug, locale, defaultLocale, model);
  }

  protected static renderContent(props: any, model: string, router: any) {
    const currentSlug = router.query.slug?.[0];
    const currentSite = process.env.NEXT_PUBLIC_REGIONAL_CARE_SITE || "fl";
    const hubBase = props.contentHubBasePath || "/content-hub";

    // If there's an error indicating the article is not tagged for this site
    if (props.error && props.error.includes('not tagged for')) {
      return (
        <main id="article-content" className="mx-auto max-w-[800px] md:mt-8 mt-4">
          <div className="container text-center py-12">
            <h1 className="text-2xl font-bold mb-4 text-primary">Content Not Available</h1>
            <p className="mb-8 text-neutral-20">
              This article is not available for the {currentSite} site.
            </p>
            <DynamicLink
              link={{ type: "url", href: hubBase }}
              className="bg-primary text-white px-6 py-3 rounded hover:bg-dark transition-colors"
            >
              Browse Available Articles
            </DynamicLink>
          </div>
        </main>
      );
    }

    // Key by article so content updates when navigating to a different article (e.g. from related articles)
    const articleKey = props.content?.id ?? currentSlug ?? router.asPath ?? "article";
    // Normal content rendering if no error
    return (
      <>
        <BuilderContent
          key={articleKey}
          content={props.content || null}
          options={{ includeRefs: true, noTraverse: true }}
          model={model}
        >
          {(content, loading, fullContent) => {
            if (loading) {
              return <div>Loading...</div>;
            }

            const heroMedia = resolveArticleHeroMedia(
              content,
              fullContent,
              props.content
            );

            const getShareUrl = () => {
              if (typeof window !== "undefined") {
                return window.location.href;
              }
              return `${process.env.NEXT_PUBLIC_SITE_URL || ""}${router.asPath}`;
            };

            // Event microdata logic
            const isEvent = content?.type === "event";
            const eventFields = content?.eventData;
            const eventData = isEvent && eventFields
              ? {
                name: content.title,
                startDate: eventFields.startDate,
                endDate: eventFields.endDate,
                description: content.description,
                image: content.media?.additionalInfo?.selectedFile?.url ? [content.media?.additionalInfo?.selectedFile?.url] : undefined,
                location: {
                  name: content.title,
                  streetAddress: [eventFields.address1, eventFields.address2].filter(Boolean).join(", "),
                  addressLocality: eventFields.city,
                  addressRegion: eventFields.state,
                  postalCode: eventFields.zipCode,
                },
              }
              : null;
            return (
              <>
                {isEvent && eventData && (
                  <EventMicrodata event={eventData} />
                )}
                <main id="article-content" className="mx-auto max-w-[800px] md:mt-8 mt-4">
                  <div className="container">
                    <div className="font-secondary font-medium text-sm pb-5">
                      <DynamicLink
                        link={{ type: "url", href: hubBase }}
                        className="text-neutral-20 hover:underline underline-offset-4 inline-flex items-center"
                      >
                        <Icon icon="chevron-left" size="sm" className="mr-1" />{" "}
                        View All Articles
                      </DynamicLink>
                    </div>
                    <div className="relative -mx-5 sm:mx-0">
                      <div className="relative w-full aspect-[16/9]">
                        <ArticleHeroMedia
                          media={heroMedia}
                          altText={content?.title || ""}
                          priority
                        />
                      </div>
                    </div>
                    <article>
                      <TaxonomyTags
                        taxonomies={content?.taxonomies}
                        taxonomyTree={props.taxonomies}
                        baseUrl={hubBase}
                      />
                      <h1 className="pt-4 text-lg md:text-3xl font-bold text-neutral-20">
                        {content?.title}
                      </h1>
                      <h4>{content?.subtitle}</h4>
                      {content?.publishDate && (
                        <div className="mt-4 font-secondary text-neutral-40">
                          {new Date(content.publishDate).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )}
                        </div>
                      )}
                      {(content?.author || fullContent?.data?.author) && (
                        <div className="mt-6 mb-8">
                          <BlogAuthorByNpi
                            authorData={content?.author?.value?.data ?? fullContent?.data?.author?.value?.data}
                            authorRefId={content?.author?.id ?? fullContent?.data?.author?.id}
                            resolvedProvider={props.resolvedAuthorProvider ?? undefined}
                          />
                        </div>
                      )}

                      <BuilderComponent
                        key="blog-content"
                        model="article"
                        content={fullContent}
                        options={{ includeRefs: true }}
                        locale={props.locale || "Default"}
                      />
                    </article>
                  </div>
                </main>

                {Array.isArray(props.ctas) && props.ctas.length > 0 && (
                  <BlogCTA
                    image={props.ctas[0]?.data?.image?.additionalInfo?.selectedFile?.url}
                    altText={props.ctas[0]?.data?.altText?.Default || ""}
                    headline={props.ctas[0]?.data?.headline?.Default || ""}
                    body={props.ctas[0]?.data?.body?.Default || ""}
                    ctaText={props.ctas[0]?.data?.ctaText?.Default || ""}
                    href={props.ctas[0]?.data?.ctaLink?.Default || ""}
                    id={props.ctas[0]?.id}
                  />
                )}

                <BlogNavigation
                  key={currentSlug}
                  previousArticle={props.previousArticle}
                  nextArticle={props.nextArticle}
                  contentHubBasePath={hubBase}
                />
                <BlogStickyShare
                  url={getShareUrl()}
                  title={content?.title}
                  summary={content?.subtitle}
                  media={getArticleHeroImageUrl(heroMedia)}
                />

                {Array.isArray(props.relatedArticles) &&
                  props.relatedArticles.length > 0 && (
                    <RelatedArticles
                      articles={props.relatedArticles}
                      heading="Related Articles"
                      contentHubBasePath={hubBase}
                      taxonomyTree={props.taxonomies}
                    />
                  )}
              </>
            );
          }}
        </BuilderContent>
      </>
    );
  }
}

// Use SectionPage
const Page = ArticlePage.createPage(ArticleWrapper);

// Export the page component
export default Page;

// Add a helper function to ensure all objects are serializable
const ensureSerializable = (obj: any): any => {
  if (obj === undefined) {
    return null;
  }

  if (obj === null) {
    return null;
  }

  if (Array.isArray(obj)) {
    return obj.map(ensureSerializable);
  }

  if (typeof obj === 'object') {
    const result: Record<string, any> = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = ensureSerializable(obj[key]);
      }
    }
    return result;
  }

  return obj;
};

// Add a helper function to extract the title correctly regardless of format
const getArticleTitle = (title: any): string => {
  if (!title) return "";

  // If it's a string, return it directly
  if (typeof title === 'string') return title;

  // If it's an object with a Default property (localized value)
  if (typeof title === 'object' && title !== null) {
    if (title['Default']) return title['Default'];

    // If it has a @type property indicating it's a localized value
    if (title['@type'] === '@builder.io/core:LocalizedValue') {
      return title['Default'] || "";
    }
  }

  // Fallback: try to convert to string
  return String(title);
};

// Implement getStaticProps
export const getStaticProps: GetStaticProps = async ({
  params,
  locale,
  defaultLocale,
}) => {
  try {
    const serviceSlug = params?.serviceSlug as string;
    if (!serviceSlug) {
      return { notFound: true, revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10) };
    }
    const contentHubBasePath = `/services/${serviceSlug}/content-hub`;
    const urlPath = `${contentHubBasePath}/${(params?.slug as string[])?.join("/") || ""}`;
    const currentLocale = locale || defaultLocale;
    const slug = (params?.slug as string[])?.[0];
    if (!slug?.trim()) {
      return { notFound: true, revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10) };
    }
    const CURRENT_SITE = normalizeSiteId(process.env.NEXT_PUBLIC_REGIONAL_CARE_SITE || "fl");

    // Store the current API key
    const originalApiKey = builder.apiKey;

    try {
      // 1. First, use the orlando API key for local site components
      builder.apiKey = process.env.NEXT_PUBLIC_BUILDER_API_KEY || "";

      const hubShell = await builder
        .get("page", {
          userAttributes: { urlPath: contentHubBasePath },
          locale: currentLocale,
          options: { includeRefs: true, enrich: true, vercelPreview: true },
        })
        .promise();

      if (!hubShell) {
        return { notFound: true, revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10) };
      }

      // Get base props from SectionPage using local builder.
      // Breadcrumbs are recomputed below from the resolved article content
      // (corp-space fetch), so skip the redundant lookup inside getBaseStaticProps.
      const baseProps = await SectionPage.getStaticProps(
        urlPath,
        slug,
        locale,
        defaultLocale,
        "article",
        { skipBreadcrumbs: true }
      );

      // 2. Switch to the corp API key for content hub data and articles
      builder.apiKey = process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || ""; // Corp API key

      // Get the specific article content directly from corp space
      let articleContent = null;
      let allArticles: any[] = [];
      let siteProperties: any = null;
      try {
        const { options: articleOptions, locale: articleLocale } = getContentHubArticleSingleFetchOptions(currentLocale);
        articleContent = resolveContentHubArticleForLocale(
          await fetchContentHubArticleBySlug(slug, {
            apiKey: process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "",
            options: articleOptions,
            locale: articleLocale,
            extraQuery: { "data.metadata.targetingScope": { $ne: "b2b" } },
          }),
          currentLocale
        );

        if (!articleContent) {
          throw new Error(`Article with slug "${slug}" not found in corp space`);
        }

        // Verify regional care site matching
        const hasRegionalCareSites = Array.isArray(articleContent.data?.regionalCareSites);
        const isTaggedForSite = hasRegionalCareSites && articleContent.data?.regionalCareSites?.some((site: any) => {
          let siteName = '';
          if (typeof site === 'string') {
            siteName = site;
          } else if (site && typeof site === 'object') {
            if (site.Name) siteName = site.Name;
            else if (site.name) siteName = site.name;
            else if (site.value) siteName = site.value;
            else if (site.id) siteName = site.id;
          }
          return normalizeSiteId(siteName) === CURRENT_SITE;
        });

        if (!isTaggedForSite) {
          console.warn(`[Content Hub] Article "${slug}" exists but is not tagged for "${CURRENT_SITE}" site. Regional care sites:`, articleContent.data?.regionalCareSites);
          return {
            notFound: true,
            revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10),
          };
        }

        const currentUrl = `${contentHubBasePath}/${(params?.slug as string[])?.join("/") || ""}`;
        const targetingScope = articleContent?.data?.metadata?.targetingScope;

        const [
          globalAlerts,
          targetAlerts,
          globalCTAs,
          targetCTAs,
          allArticlesResult,
          sitePropertiesResult,
        ] = await Promise.all([
          builder.getAll("alert", { userAttributes: { scope: "global" }, options: { locale: currentLocale } }).catch(() => []),
          builder.getAll("alert", { userAttributes: { scope: targetingScope }, options: { locale: currentLocale } }).catch(() => []),
          builder.getAll("cta", { userAttributes: { scope: "global", urlPath: currentUrl }, options: { locale: currentLocale } }).catch(() => []),
          builder.getAll("cta", { userAttributes: { scope: targetingScope, urlPath: currentUrl } }).catch(() => []),
          getAllContentHubArticlesCached(currentLocale || "en").catch(() => []),
          builder.get("site-properties", {
            locale: locale || "en",
            query: { name: "corp" },
            options: { includeRefs: true, vercelPreview: true, locale: locale || "en", enrich: true },
          }).promise().catch(() => null),
        ]);

        allArticles = prepareContentHubArticles(allArticlesResult, currentLocale);
        siteProperties = sitePropertiesResult;

        builder.apiKey = originalApiKey;

        const alerts = [...globalAlerts, ...targetAlerts];
        const ctas = [...globalCTAs, ...targetCTAs];
        baseProps.props.alerts = alerts;
        baseProps.props.ctas = ctas;
      } catch (articleError) {
        console.error(`Error fetching article content: ${articleError instanceof Error ? articleError.message : String(articleError)}`);
      }

      if (!articleContent) {
        builder.apiKey = originalApiKey;
        return { notFound: true, revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10) };
      }

      // 3. Restore the original API key (already done inside the try block above, but kept as safety)
      builder.apiKey = originalApiKey;

      // Hub taxonomy: scoped to this service content hub page.
      builder.apiKey = process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "";
      let taxonomyData = null;
      const scopedHubTaxonomyRef =
        hubShell?.data?.taxonomy?.referenceId ??
        (hubShell?.data as { taxonomies?: { referenceId?: string } } | undefined)?.taxonomies
          ?.referenceId;

      if (scopedHubTaxonomyRef) {
        taxonomyData = await builder.get("taxonomy", {
          locale: currentLocale,
          options: { includeRefs: true },
          query: { id: scopedHubTaxonomyRef },
          apiKey: process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "",
        }).promise();
      } else if (siteProperties?.data?.taxonomies?.id) {
        taxonomyData = await builder.get("taxonomy", {
          locale: currentLocale,
          options: { includeRefs: true },
          query: { id: siteProperties.data.taxonomies.id },
          apiKey: process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "",
        }).promise();
      } else if (siteProperties?.data?.taxonomies?.taxonomies) {
        taxonomyData = { data: siteProperties.data.taxonomies };
      } else {
        taxonomyData = await builder.get("taxonomy", {
          locale: currentLocale,
          options: { includeRefs: true },
          query: { name: "content-hub" },
        }).promise();
      }
      builder.apiKey = originalApiKey;

      const mergedHubTaxonomies =
        mergeTaxonomyTree(
          taxonomyData?.data?.taxonomies,
          locale || defaultLocale
        ) || [];

      // Filter articles by regionalCareSites
      const filteredArticles = allArticles.filter((article: any) => {
        if (!article.data?.regionalCareSites) {
          return false;
        }

        return Array.isArray(article.data.regionalCareSites) &&
          article.data.regionalCareSites.some((site: any) => {
            let siteName = '';
            if (typeof site === 'string') {
              siteName = site;
            } else if (site && typeof site === 'object') {
              if (site.Name) siteName = site.Name;
              else if (site.name) siteName = site.name;
              else if (site.value) siteName = site.value;
              else if (site.id) siteName = site.id;
            }
            return normalizeSiteId(siteName) === CURRENT_SITE;
          });
      });

      if (
        articleContent &&
        mergedHubTaxonomies.length > 0 &&
        !articleBelongsToTaxonomyTree(
          articleContent.data?.taxonomies,
          mergedHubTaxonomies
        )
      ) {
        return {
          notFound: true,
          revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10),
        };
      }

      // Site/taxonomy tagging for *this* article was already verified. If the
      // bulk list is stale, include the current article instead of 404ing.
      const hubArticles = ensureCurrentArticleInList(
        mergedHubTaxonomies.length > 0
          ? filteredArticles.filter((article: any) =>
              articleBelongsToTaxonomyTree(
                article.data?.taxonomies,
                mergedHubTaxonomies
              )
            )
          : filteredArticles,
        articleContent,
        slug
      );

      // Sort articles by date
      const sortedArticles = hubArticles
        .filter((article: any) => article.data?.slug && article.data?.title)
        .sort((a: any, b: any) => {
          const dateA = new Date(a.data?.publishDate || 0).getTime();
          const dateB = new Date(b.data?.publishDate || 0).getTime();
          return dateB - dateA;
        });

      // Find current article index
      const currentIndex = sortedArticles.findIndex(
        (article: any) => contentHubSlugsEqual(article.data?.slug, slug)
      );

      // Get previous and next articles
      let previousArticle =
        currentIndex !== -1 && currentIndex < sortedArticles.length - 1
          ? {
            slug: sortedArticles[currentIndex + 1].data?.slug || "",
            title: getArticleTitle(sortedArticles[currentIndex + 1].data?.title),
          }
          : null;

      let nextArticle =
        currentIndex > 0
          ? {
            slug: sortedArticles[currentIndex - 1].data?.slug || "",
            title: getArticleTitle(sortedArticles[currentIndex - 1].data?.title),
          }
          : null;

      // Get related articles based on taxonomies
      const currentArticle = sortedArticles[currentIndex];
      const relatedArticles = sortedArticles
        .filter((article: any, index: number) => {
          // Skip current article
          if (index === currentIndex) return false;

          // Extract hub-scoped taxonomies from both articles
          const articleTaxonomies = getHubTaxonomyTags(
            article.data?.taxonomies,
            mergedHubTaxonomies
          );
          const currentArticleTaxonomies = getHubTaxonomyTags(
            currentArticle?.data?.taxonomies,
            mergedHubTaxonomies
          );

          // If either array is empty, don't consider a match
          if (articleTaxonomies.length === 0 || currentArticleTaxonomies.length === 0) {
            return false;
          }

          // Check if there are matching tags
          return articleTaxonomies.some((tag: string) =>
            currentArticleTaxonomies.includes(tag)
          );
        })
        .slice(0, 3)
        .map((article: any) => {
          // Determine the media type
          const mediaType = typeof article.data?.media === 'string' ? 'url' : 'asset';
          return {
            title: getArticleTitle(article.data?.title),
            slug: article.data?.slug || "",
            media: article.data?.media || null,
            mediaType,
            authorId: article.data?.author?.id ?? null,
            author: article.data?.author?.value?.data
              ? {
                name: [
                  article.data.author.value.data.firstName || "",
                  article.data.author.value.data.middleInitial || "",
                  article.data.author.value.data.lastName || "",
                ]
                  .filter(Boolean)
                  .join(" "),
                href: "",
              }
              : null,
            taxonomies: article.data?.taxonomies,
          };
        });

      // Also check for navigation links if none found
      if (!previousArticle && !nextArticle && hubArticles.length > 1) {
        // Find the current index in the filtered articles
        const currentFilteredIndex = hubArticles.findIndex(
          (article: any) => contentHubSlugsEqual(article.data?.slug, slug)
        );

        // If found, get previous and next
        if (currentFilteredIndex !== -1) {
          if (currentFilteredIndex < hubArticles.length - 1) {
            previousArticle = {
              slug: hubArticles[currentFilteredIndex + 1].data?.slug || "",
              title: getArticleTitle(hubArticles[currentFilteredIndex + 1].data?.title),
            };
          }

          if (currentFilteredIndex > 0) {
            nextArticle = {
              slug: hubArticles[currentFilteredIndex - 1].data?.slug || "",
              title: getArticleTitle(hubArticles[currentFilteredIndex - 1].data?.title),
            };
          }
        }
      }
      builder.apiKey = process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "";

      // SSG: resolve author by NPI from Algolia so author data is in the initial HTML
      let resolvedAuthorProvider = null;
      const authorNpi = articleContent?.data?.author?.value?.data?.npi?.trim();
      if (authorNpi) {
        try {
          const hit = await findProviderByNpi(authorNpi);
          if (hit?.slug) {
            resolvedAuthorProvider = {
              firstName: hit.FirstName,
              lastName: hit.LastName,
              middleInitial: hit.MiddleInitial,
              slug: hit.slug,
              title: hit.Title,
              media: hit.media ?? undefined,
            };
          }
        } catch {
          // ignore; client will fall back to hub or Builder
        }
      }

      if (articleContent) {
        baseProps.props.breadcrumbs = await buildBreadcrumbsFromPage(
          urlPath,
          articleContent,
          baseProps.props.builderLocale ?? currentLocale
        );
      }

      return {
        props: {
          ...baseProps.props,
          content: articleContent || null, // Use the corp article content
          taxonomies: mergedHubTaxonomies.length > 0 ? mergedHubTaxonomies : null,
          previousArticle: previousArticle || null,
          nextArticle: nextArticle || null,
          relatedArticles: relatedArticles || [],
          resolvedAuthorProvider,
          contentHubBasePath,
          serviceHubShellContent: hubShell,
        },
        revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10),
      };
    } catch (innerError) {
      console.error(`Error in getStaticProps inner try/catch: ${innerError instanceof Error ? innerError.message : String(innerError)}`);
      // Ensure we restore the API key even if there's an error
      builder.apiKey = originalApiKey;

      // If the error indicates the article is not tagged for this site, return notFound
      if (innerError instanceof Error && innerError.message.includes('not tagged for')) {
        return {
          notFound: true,
          revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10),
        };
      }

      // Return minimal props to avoid complete page failure for other errors
      return {
        props: ensureSerializable({
          content: null,
          taxonomies: [],
          previousArticle: null,
          nextArticle: null,
          relatedArticles: [],
          error: `Failed to load article data: ${innerError instanceof Error ? innerError.message : String(innerError)}`
        }),
        revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10),
      };
    }
  } catch (outerError) {
    console.error(`Critical error in getStaticProps: ${outerError instanceof Error ? outerError.message : String(outerError)}`);
    
    // If the error indicates the article is not tagged for this site, return notFound
    if (outerError instanceof Error && outerError.message.includes('not tagged for')) {
      return {
        notFound: true,
        revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10),
      };
    }
    
    // Return minimal props to avoid complete page failure for other errors
    return {
      props: ensureSerializable({
        content: null,
        taxonomies: [],
        previousArticle: null,
        nextArticle: null,
        relatedArticles: [],
        error: `Critical error loading page: ${outerError instanceof Error ? outerError.message : String(outerError)}`
      }),
      revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10),
    };
  }
};

export const getStaticPaths: GetStaticPaths = async ({
  locales = ["en_US"],
}) => {
  if (process.env.SKIP_SSG === "true") {
    return { paths: [], fallback: "blocking" };
  }
  const paths: { params: { serviceSlug: string; slug: string[] }; locale: string }[] = [];
  const CURRENT_SITE = normalizeSiteId(process.env.NEXT_PUBLIC_REGIONAL_CARE_SITE || "fl");

  const originalApiKey = builder.apiKey;

  builder.apiKey = process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "";

  const flApiKey = process.env.NEXT_PUBLIC_BUILDER_API_KEY || "";
  const corpApiKey = process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "";

  for (const locale of locales) {
    const articles = prepareContentHubArticles(
      (await getAllBuilderContent(
        "article",
        getContentHubArticleFetchOptions(locale, {
          fields: "data.slug,data.regionalCareSites,data.metadata,data.title,data.taxonomies",
          options: { includeRefs: true, noTargeting: true },
        })
      )) as any[],
      locale
    );

    const filteredArticles = articles.filter((article: any) => {
      if (!article.data?.slug) return false;
      if (!article.data?.regionalCareSites) return false;

      return (
        Array.isArray(article.data.regionalCareSites) &&
        article.data.regionalCareSites.some((site: any) => {
          let siteName = "";
          if (typeof site === "string") {
            siteName = site;
          } else if (site && typeof site === "object") {
            if (site.Name) siteName = site.Name;
            else if (site.name) siteName = site.name;
            else if (site.value) siteName = site.value;
            else if (site.id) siteName = site.id;
          }
          return normalizeSiteId(siteName) === CURRENT_SITE;
        })
      );
    });

    builder.apiKey = process.env.NEXT_PUBLIC_BUILDER_API_KEY || "";
    const serviceSlugs = await getServiceContentHubSlugs(locale);
    builder.apiKey = corpApiKey;

    // Resolve each service hub's taxonomy concurrently. getTaxonomyForBuild
    // passes its API keys explicitly (no reliance on the global builder.apiKey)
    // and memoizes per hub, so fanning out is safe and cuts build-time Builder
    // round-trips from sequential to (locales × max-hub-latency).
    const perHubPaths = await Promise.all(
      serviceSlugs.map(async (serviceSlug) => {
        const hubPath = `/services/${serviceSlug}/content-hub`;
        const { taxonomies: hubTaxonomies } = await getTaxonomyForBuild(
          flApiKey,
          corpApiKey,
          locale,
          hubPath
        );
        const hubArticles =
          hubTaxonomies.length > 0
            ? filteredArticles.filter((article: any) =>
                articleBelongsToTaxonomyTree(
                  article.data?.taxonomies,
                  hubTaxonomies
                )
              )
            : filteredArticles;

        return hubArticles.map((article: any) => ({
          params: {
            serviceSlug,
            slug: [article.data?.slug],
          },
          locale,
        }));
      })
    );

    for (const hubPaths of perHubPaths) {
      paths.push(...hubPaths);
    }
  }

  builder.apiKey = originalApiKey;

  return {
    paths,
    fallback: "blocking",
  };
};
